import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Globe, Eye, MousePointerClick, Users, TrendingUp,
  Monitor, Smartphone, Tablet, ArrowUpRight, RefreshCw,
  BarChart3, ExternalLink, Brain, Zap, AlertTriangle,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CampaignRoiTab from '../components/CampaignRoiTab';
import PopupAbTab from '../components/PopupAbTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebsiteAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState('overview');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/website-analytics/dashboard?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      toast.error('Fehler beim Laden der Analytics-Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [days]);

  const deviceIcon = (device) => {
    const d = (device || '').toLowerCase();
    if (d.includes('mobile') || d.includes('phone')) return <Smartphone className="w-4 h-4" />;
    if (d.includes('tablet') || d.includes('ipad')) return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const overviewBody = loading ? (
    <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
    </div>
  ) : !data ? (
    <div className="text-center py-16 text-gray-500" data-testid="analytics-no-data">
      <Globe className="w-12 h-12 mx-auto mb-4 opacity-40" />
      <p>Keine Analytics-Daten vorhanden.</p>
      <p className="text-sm mt-1">Integriere das Tracking-Script auf www.strongfit.ch</p>
    </div>
  ) : null;

  const maxDailyPV = Math.max(...((data?.daily_breakdown) || []).map(d => d.pageviews), 1);

  return (
    <div className="space-y-6" data-testid="website-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="analytics-title">
            <Globe className="w-6 h-6 text-amber-600" />
            Website Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-1">Traffic und Conversions von www.strongfit.ch</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 30, 90].map((d) => (
            <Button
              key={d}
              variant={days === d ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(d)}
              data-testid={`analytics-period-${d}`}
              className={days === d ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            >
              {d}T
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="analytics-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tab-Bar */}
      <div className="flex gap-1 border-b border-gray-200" data-testid="analytics-tab-bar">
        {[
          { id: 'overview', label: 'Übersicht' },
          { id: 'roi', label: 'Kampagnen-ROI' },
          { id: 'popup', label: 'Popup A/B-Test' },
          { id: 'newsletterab', label: 'Newsletter A/B' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`analytics-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'roi' && <CampaignRoiTab days={days} />}
      {tab === 'popup' && <PopupAbTab testKey="popup" />}
      {tab === 'newsletterab' && <PopupAbTab testKey="newsletter" />}
      {tab === 'overview' && overviewBody}
      {tab === 'overview' && data && !loading && (
        <>

      {/* KPI Karten */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          title="Seitenaufrufe"
          value={data.total_pageviews}
          delta={data.comparison?.pageviews?.delta}
          icon={Eye}
          color="blue"
          testId="kpi-pageviews"
        />
        <KpiCard
          title="Sessions"
          value={data.unique_sessions}
          delta={data.comparison?.sessions?.delta}
          icon={Users}
          color="green"
          testId="kpi-sessions"
        />
        <KpiCard
          title="Besucher"
          value={data.unique_visitors}
          delta={data.comparison?.visitors?.delta}
          icon={Users}
          color="purple"
          testId="kpi-visitors"
        />
        <KpiCard
          title="Conversions"
          value={data.total_conversions}
          delta={data.comparison?.conversions?.delta}
          icon={MousePointerClick}
          color="amber"
          testId="kpi-conversions"
        />
        <KpiCard
          title="Conv.-Rate"
          value={`${data.conversion_rate}%`}
          delta={data.comparison?.conversion_rate?.delta}
          icon={TrendingUp}
          color="emerald"
          testId="kpi-conversion-rate"
        />
      </div>

      {/* KI-Performance */}
      {data.ai_metrics && (
        <Card data-testid="analytics-ai-performance">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="w-5 h-5 text-violet-600" />
              KI-Performance ({days} Tage)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                title="KI-Anfragen"
                value={data.ai_metrics.total}
                delta={data.ai_metrics.comparison?.total?.delta}
                icon={Zap}
                color="purple"
                testId="kpi-ai-requests"
              />
              <KpiCard
                title="Erfolgsrate"
                value={`${data.ai_metrics.success_rate}%`}
                delta={data.ai_metrics.comparison?.success_rate?.delta}
                icon={TrendingUp}
                color="emerald"
                testId="kpi-ai-success-rate"
              />
              <KpiCard
                title="Latenz (ms)"
                value={data.ai_metrics.avg_latency}
                delta={data.ai_metrics.comparison?.avg_latency?.delta}
                icon={BarChart3}
                color="blue"
                invertDelta
                testId="kpi-ai-latency"
              />
              <KpiCard
                title="Fehler"
                value={data.ai_metrics.errors}
                delta={data.ai_metrics.comparison?.errors?.delta}
                icon={AlertTriangle}
                color="amber"
                invertDelta
                testId="kpi-ai-errors"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Traffic-Chart */}
      <Card data-testid="analytics-daily-chart">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Täglicher Traffic ({days} Tage)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data.daily_breakdown || []).length > 0 ? (
            <div className="space-y-1">
              {data.daily_breakdown.map((day) => (
                <div key={day.date} className="flex items-center gap-3 text-sm">
                  <span className="w-20 text-gray-500 text-xs shrink-0">
                    {new Date(day.date + 'T00:00:00').toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}
                  </span>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all"
                        style={{ width: `${Math.max((day.pageviews / maxDailyPV) * 100, 2)}%` }}
                      />
                    </div>
                    <span className="w-12 text-right font-medium text-gray-700">{day.pageviews}</span>
                  </div>
                  {day.conversions > 0 && (
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded font-medium">
                      {day.conversions} Conv.
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">Keine Daten im gewählten Zeitraum</p>
          )}
        </CardContent>
      </Card>

      {/* Untere Karten: Top Seiten, Quellen, Geräte, Conversions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top Seiten */}
        <Card data-testid="analytics-top-pages">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-blue-500" />
              Top Seiten
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data.top_pages || []).length > 0 ? (
              <div className="space-y-2">
                {data.top_pages.map((p, i) => (
                  <div key={p.page} className="flex items-center justify-between text-sm">
                    <span className="truncate text-gray-700 mr-2">
                      <span className="text-gray-400 mr-1.5">{i + 1}.</span>
                      {p.page}
                    </span>
                    <span className="font-medium text-gray-900 shrink-0">{p.views}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Keine Daten</p>
            )}
          </CardContent>
        </Card>

        {/* Traffic-Quellen */}
        <Card data-testid="analytics-traffic-sources">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              Traffic-Quellen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data.traffic_sources || []).length > 0 ? (
              <div className="space-y-2">
                {data.traffic_sources.map((s) => {
                  const pct = data.total_pageviews > 0 ? Math.round((s.count / data.total_pageviews) * 100) : 0;
                  return (
                    <div key={s.source} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 truncate mr-2">
                        <span className="capitalize text-gray-700">{s.source}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-16 bg-gray-100 rounded-full h-2">
                          <div className="h-full bg-green-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-medium text-gray-900 w-10 text-right">{s.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Keine Daten</p>
            )}
          </CardContent>
        </Card>

        {/* Geräte */}
        <Card data-testid="analytics-devices">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Monitor className="w-4 h-4 text-purple-500" />
              Geräte
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data.device_breakdown || []).length > 0 ? (
              <div className="space-y-3">
                {data.device_breakdown.map((d) => {
                  const pct = data.total_pageviews > 0 ? Math.round((d.count / data.total_pageviews) * 100) : 0;
                  return (
                    <div key={d.device} className="flex items-center gap-3 text-sm">
                      {deviceIcon(d.device)}
                      <span className="capitalize text-gray-700 flex-1">{d.device}</span>
                      <span className="text-gray-500">{pct}%</span>
                      <span className="font-medium text-gray-900 w-10 text-right">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Keine Daten</p>
            )}
          </CardContent>
        </Card>

        {/* Regionen (GeoIP) */}
        <Card data-testid="analytics-regions">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-4 h-4 text-teal-500" />
              Regionen der Besucher
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data.top_regions || []).length > 0 ? (
              <div className="space-y-2">
                {data.top_regions.map((r) => (
                  <div key={`${r.city}-${r.country_code}`} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate mr-2">
                      {r.city}{r.region ? `, ${r.region}` : ''}
                      {r.country_code && r.country_code !== 'CH' && (
                        <span className="text-gray-400 ml-1">({r.country_code})</span>
                      )}
                    </span>
                    <span className="font-medium text-gray-900 shrink-0 bg-teal-50 px-2 py-0.5 rounded">{r.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Noch keine Regions-Daten (werden ab jetzt bei neuen Besuchern erfasst)</p>
            )}
          </CardContent>
        </Card>

        {/* Conversion-Typen */}
        <Card data-testid="analytics-conversion-types">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 text-amber-500" />
              Conversion-Typen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data.conversion_types || []).length > 0 ? (
              <div className="space-y-2">
                {data.conversion_types.map((c) => (
                  <div key={c.event_type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate mr-2">{_conversionLabel(c.event_type)}</span>
                    <span className="font-medium text-gray-900 shrink-0 bg-amber-50 px-2 py-0.5 rounded">{c.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Noch keine Conversions</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Referrers */}
      <Card data-testid="analytics-referrers">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-teal-500" />
            Top Referrers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data.top_referrers || []).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.top_referrers.map((r) => (
                <div key={r.referrer} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2">
                  <span className="truncate text-gray-700 mr-2">{r.referrer}</span>
                  <span className="font-medium text-gray-900 shrink-0">{r.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Keine Referrer-Daten</p>
          )}
        </CardContent>
      </Card>

      {/* Integration-Hinweis */}
      <Card className="border-dashed border-gray-300 bg-gray-50/50" data-testid="analytics-integration-hint">
        <CardContent className="py-5">
          <p className="text-sm text-gray-600 font-medium mb-2">Integration auf www.strongfit.ch</p>
          <p className="text-xs text-gray-500 leading-relaxed">
            Füge das folgende Tracking-Script auf deiner Marketing-Website ein, um Pageviews und Conversions zu erfassen.
            Pageview-Daten werden automatisch bei jedem Seitenaufruf gesendet. Conversion-Events können über
            <code className="mx-1 bg-gray-200 px-1 rounded">window.parent.postMessage</code> aus dem Buchungs-iFrame gesendet werden.
          </p>
          <pre className="mt-3 bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto">
{`<script>
// StrongFit Analytics Tracker
(function() {
  var API = "${process.env.REACT_APP_BACKEND_URL}/api/website-analytics";
  var sid = sessionStorage.getItem('sf_sid') || crypto.randomUUID();
  sessionStorage.setItem('sf_sid', sid);
  var vid = localStorage.getItem('sf_vid') || crypto.randomUUID();
  localStorage.setItem('sf_vid', vid);

  // Pageview
  fetch(API + '/pageview', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      page: location.pathname,
      referrer: document.referrer,
      device: /Mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      browser: navigator.userAgent.split(' ').pop(),
      screen_width: screen.width,
      session_id: sid,
      visitor_id: vid
    })
  }).catch(function(){});

  // Listen for conversion postMessages from booking iframe
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'strongfit_conversion') {
      fetch(API + '/conversion', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          event_type: e.data.event_type || 'booking_completed',
          page: location.pathname,
          session_id: sid,
          visitor_id: vid,
          value: e.data.value,
          metadata: e.data.metadata
        })
      }).catch(function(){});
    }
  });

  // Generic event tracking (Scroll, Video, Form, Click)
  window.sfTrackEvent = function(name, opts) {
    fetch(API + '/event', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(Object.assign({
        event_name: name,
        page: location.pathname,
        session_id: sid,
        visitor_id: vid
      }, opts || {}))
    }).catch(function(){});
  };

  // KI-Chatbot interaction tracking (call this nach jeder KI-Antwort):
  // sfTrackChatbot({ latency_ms: 850, success: true, model: 'gpt-4o-mini' });
  window.sfTrackChatbot = function(data) {
    fetch(API + '/chatbot/interaction', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(Object.assign({
        page: location.pathname,
        session_id: sid,
        visitor_id: vid,
        success: true
      }, data || {}))
    }).catch(function(){});
  };
})();
</script>`}
          </pre>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
};

// KPI-Card Hilfskomponente
const KpiCard = ({ title, value, delta, icon: Icon, color, testId, invertDelta = false }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'text-blue-500' },
    green: { bg: 'bg-green-50', text: 'text-green-600', icon: 'text-green-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'text-purple-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'text-amber-500' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'text-emerald-500' },
  };
  const c = colorMap[color] || colorMap.blue;

  const isPositive = invertDelta ? delta < 0 : delta > 0;
  const isNegative = invertDelta ? delta > 0 : delta < 0;

  return (
    <Card className={`${c.bg} border-${color}-200`} data-testid={testId}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
        <p className={`text-2xl font-bold mt-1 ${c.text}`}>{value}</p>
        {delta !== null && delta !== undefined && (
          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isPositive ? 'text-emerald-600' : isNegative ? 'text-red-500' : 'text-gray-400'}`} data-testid={`${testId}-delta`}>
            {delta > 0 ? <ArrowUp className="w-3 h-3" /> : delta < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            <span>{delta > 0 ? '+' : ''}{delta}%</span>
            <span className="text-gray-400 font-normal ml-0.5">vs. Vorperiode</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Conversion-Label Übersetzung
function _conversionLabel(eventType) {
  const labels = {
    booking_completed: 'Buchung abgeschlossen',
    trial_signup: 'Probetraining gebucht',
    contact_form: 'Kontaktformular',
    newsletter_signup: 'Newsletter-Anmeldung',
    phone_click: 'Anruf-Klick',
    download: 'Download',
  };
  return labels[eventType] || eventType;
}

export default WebsiteAnalyticsPage;
